/**
 * NfcAgent REST client — connects directly to the NfcAgent local service.
 * Runs in the browser (localhost CORS).
 */
const DEFAULT_URL = 'http://127.0.0.1:38741';
const STORAGE_KEY = 'nfcagent-url';
const TIMEOUT_MS = 3000;
// Longer than the agent can take to answer: a card hold it may wait 3 s for, a SAM lease it may wait
// 20 s for, then the write. Giving up first hides its refusal, or the report of a card it did write.
export const ENCODE_TIMEOUT_MS = 30_000;
const CLAIM_HEADER = 'X-Corteh-Claim-Id';
export class NfcAgentApiError extends Error {
    status;
    refusal;
    constructor(status, refusal) {
        super(refusal.message || refusal.error || `NfcAgent request failed (${status})`);
        this.status = status;
        this.refusal = refusal;
        this.name = 'NfcAgentApiError';
    }
}
/** Get the configured NfcAgent URL (from localStorage or default). */
export function getNfcAgentUrl() {
    if (typeof window !== 'undefined') {
        return localStorage.getItem(STORAGE_KEY) || DEFAULT_URL;
    }
    return DEFAULT_URL;
}
/** Set the NfcAgent URL (stored in localStorage). */
export function setNfcAgentUrl(url) {
    if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, url);
        window.dispatchEvent(new Event('bms:nfc-agent-config-changed'));
    }
}
async function fetchAgent(path, options, context = {}, timeoutMs = TIMEOUT_MS) {
    const base = getNfcAgentUrl();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const headers = { 'Content-Type': 'application/json', ...options?.headers };
        if (context.claimId != null) {
            headers[CLAIM_HEADER] = context.claimId.toString();
        }
        const res = await fetch(`${base}${path}`, {
            ...options,
            signal: controller.signal,
            headers,
        });
        if (!res.ok) {
            const body = await res.json().catch(() => null);
            throw new NfcAgentApiError(res.status, body ?? {
                error: 'agent_request_failed',
                message: `NfcAgent request failed (${res.status})`,
            });
        }
        return await res.json();
    }
    finally {
        clearTimeout(timer);
    }
}
// ---------------------------------------------------------------------------
// REST API methods
// ---------------------------------------------------------------------------
export async function getHealth() {
    return fetchAgent('/health');
}
export async function getReaders() {
    return fetchAgent('/readers');
}
export async function getReader(id) {
    return fetchAgent(`/reader/${encodeURIComponent(id)}`);
}
export async function getSamInfo() {
    return fetchAgent('/sam/info');
}
export async function getSamBindingStatus(context) {
    return fetchAgent('/sam/binding/status', undefined, context);
}
export async function getConfig() {
    return fetchAgent('/config');
}
export async function getSamKeyStatus() {
    return fetchAgent('/sam/keys/status');
}
/**
 * The write request NfcAgent takes, exactly as Edge MACed it: nothing is added, dropped or
 * recomputed in the browser — BMS-spec docs/access/cards.md §Write authentication (R1).
 */
export function encodeRequestFromPayload(payload) {
    return {
        macFormat: payload.macFormat,
        cardUid: payload.cardUid,
        payloadHex: payload.payload,
        signingPublicKeyHex: payload.signingPublicKeyHex,
        macKeyVersion: payload.macKeyVersion,
        requestMac: payload.requestMac,
    };
}
/**
 * An operator-facing reading of an encode refusal, for the R1 codes that
 * change what to do next. Both in-repo callers (`IssueCardModal`,
 * `CardTapHandler`) have moved to `encodeErrorKey()` + `t()`, which is the
 * translated equivalent of this function (the four known codes hold identical
 * English by design). This one stays English-only and is kept only for a
 * caller outside this repo that has not migrated — a new caller should use
 * `encodeErrorKey()` instead, never this one.
 */
export function describeEncodeError(error) {
    switch (error) {
        case 'report_mac_unavailable':
            return 'The card may have been written, but the encoder could not authenticate its report. Keep the card, '
                + 'do not hand it out, read it and look it up in Edge before encoding it again.';
        case 'write_request_mac_invalid':
            return 'The encoder refused the write request: its SAM does not hold this site\'s current card key, or the '
                + 'request was altered. Nothing was written.';
        case 'write_mac_format_unsupported':
            return 'Edge and NfcAgent run incompatible versions. Update NfcAgent. Nothing was written.';
        case 'card_uid_mismatch':
            return 'The card on the reader is not the card the request was prepared for. Nothing was written.';
        default:
            return error ?? 'Unknown error';
    }
}
const KNOWN_ENCODE_ERRORS = [
    'report_mac_unavailable',
    'write_request_mac_invalid',
    'write_mac_format_unsupported',
    'card_uid_mismatch',
];
/**
 * The `access.encodeError.*` key for a caller that translates — `t()` on the
 * result reads exactly `describeEncodeError()`'s English by default. An
 * unrecognised, non-null code (a server error this client does not yet know)
 * is returned as-is: `t()` renders an unknown key as the key itself, which is
 * the same "show the raw code" fallback `describeEncodeError()` uses.
 */
export function encodeErrorKey(error) {
    if (error && KNOWN_ENCODE_ERRORS.includes(error)) {
        return `access.encodeError.${error}`;
    }
    return error ?? 'access.encodeError.unknown';
}
export async function encodeCard(readerId, req, context) {
    return fetchAgent(`/reader/${encodeURIComponent(readerId)}/encode`, {
        method: 'POST',
        body: JSON.stringify(req),
    }, context, ENCODE_TIMEOUT_MS);
}
// ---------------------------------------------------------------------------
// Connection status helper
// ---------------------------------------------------------------------------
export async function checkStatus() {
    try {
        const health = await getHealth();
        if (!health.running)
            return { status: 'disconnected' };
        try {
            const sam = await getSamInfo();
            return { status: sam.present ? 'connected' : 'no-sam', health, sam };
        }
        catch {
            return { status: 'no-sam', health };
        }
    }
    catch {
        return { status: 'disconnected' };
    }
}
//# sourceMappingURL=client.js.map